import { MoreHorizontal } from "lucide-react";
import { Fragment, type MouseEvent, type ReactNode } from "react";
import { IconButton } from "../../components/IconButton/IconButton";
import { ContextMenu } from "../../components/Menu/ContextMenu";

export interface BreadcrumbItem {
  label: string;
  /** Where the crumb links to. A crumb without `href` or `onSelect` renders as plain text. */
  href?: string;
  /**
   * Called on a plain click (no modifier keys), e.g. to navigate with a client-side
   * router. The default link behavior is prevented; modified clicks still open `href`.
   */
  onSelect?: () => void;
}

export interface BreadcrumbsProps {
  /** The ancestors, outermost first. Each one should link somewhere. */
  items: BreadcrumbItem[];
  /** The current page. Never a link. */
  current: string;
  /** Accessible name for the landmark. Defaults to "Breadcrumb". */
  label?: string;
  /** Accessible name for the "…" button that opens the hidden levels. Defaults to "Show hidden levels". */
  moreLabel?: string;
}

const crumbText = "[font:var(--type-body-xs)] text-content-tertiary";

function isPlainClick(e: MouseEvent) {
  return e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;
}

function Crumb({ item }: Readonly<{ item: BreadcrumbItem }>) {
  const className = `${crumbText} block truncate`;
  if (item.href) {
    return (
      <a
        href={item.href}
        onClick={(e) => {
          if (!item.onSelect || !isPlainClick(e)) return;
          e.preventDefault();
          item.onSelect();
        }}
        className={`${className} cursor-pointer rounded transition-colors hover:text-content-primary btn-focus-ring`}
      >
        {item.label}
      </a>
    );
  }
  if (item.onSelect) {
    return (
      <button
        type="button"
        onClick={item.onSelect}
        className={`${className} cursor-pointer rounded transition-colors hover:text-content-primary btn-focus-ring`}
      >
        {item.label}
      </button>
    );
  }
  return <span className={className}>{item.label}</span>;
}

function Separator() {
  return (
    <span aria-hidden="true" className={crumbText}>
      /
    </span>
  );
}

function HiddenLevels({ items, label }: Readonly<{ items: BreadcrumbItem[]; label: string }>) {
  const menuItems = items.map((item) => ({
    label: item.label,
    onSelect: () => {
      if (item.onSelect) item.onSelect();
      else if (item.href) window.location.assign(item.href);
    },
  }));
  return (
    <ContextMenu items={menuItems}>
      {(openFrom, isOpen) => (
        <IconButton
          icon={<MoreHorizontal />}
          aria-label={label}
          aria-haspopup="menu"
          aria-expanded={isOpen}
          variant="ghost"
          size="sm"
          onClick={(event) => openFrom(event.currentTarget)}
        />
      )}
    </ContextMenu>
  );
}

/**
 * The trail on its own (no bar chrome), for placing in a header. Up to three crumbs show in full, counting the
 * current page. A longer trail keeps the first crumb and the last parent, and the levels between them open from
 * a "…" button. Below `md` only the immediate parent is shown, prefixed with "…", so the trail fits a phone.
 */
export function Breadcrumbs({
  items,
  current,
  label = "Breadcrumb",
  moreLabel = "Show hidden levels",
}: Readonly<BreadcrumbsProps>) {
  const last = items.length - 1;
  const hidden = items.length >= 3 ? items.slice(1, last) : [];
  const shown = hidden.length > 0 ? [items[0], items[last]] : items;
  const lastShown = shown.length - 1;
  return (
    <nav aria-label={label} className="flex min-w-0 flex-1 items-center">
      <ol className="flex min-w-0 items-center gap-2">
        {last > 0 && (
          <li aria-hidden="true" className="flex shrink-0 items-center gap-2 md:hidden">
            <span className={crumbText}>…</span>
            <Separator />
          </li>
        )}
        {shown.map((item, i) => (
          <Fragment
            // biome-ignore lint/suspicious/noArrayIndexKey: crumbs are positional
            key={i}
          >
            <li className={`${i < lastShown ? "hidden md:flex" : "flex"} min-w-0 max-w-48 shrink items-center gap-2`}>
              <Crumb item={item} />
              <Separator />
            </li>
            {i === 0 && hidden.length > 0 && (
              <li className="hidden shrink-0 items-center gap-2 md:flex">
                <HiddenLevels items={hidden} label={moreLabel} />
                <Separator />
              </li>
            )}
          </Fragment>
        ))}
        <li className="flex min-w-0 items-center">
          <span
            aria-current="page"
            className="min-w-0 truncate [font:var(--type-body-xs)] ![font-weight:var(--weight-medium)] text-content-primary"
          >
            {current}
          </span>
        </li>
      </ol>
    </nav>
  );
}

export interface BreadcrumbBarProps extends BreadcrumbsProps {
  /** Page-wide actions, right-aligned. */
  actions?: ReactNode;
}

/** A 48px bar at the top of a page below a top-level destination: the trail plus page actions. */
export function BreadcrumbBar({ actions, ...trail }: Readonly<BreadcrumbBarProps>) {
  return (
    <div className="flex h-12 shrink-0 items-center gap-2 border-b border-border-subtle bg-surface-primary px-4">
      <Breadcrumbs {...trail} />
      {actions && <div className="ml-auto flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  );
}
